// src/pages/ExecutiveBriefingPage.tsx
import React, { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { summariesApi, forecastsApi, performanceApi, dashboardApi } from '../api';
import {
  Box, Typography, CircularProgress, Alert, Paper, Grid,
  Container, Card, CardContent, Divider, Chip, List,
  ListItem, ListItemIcon, ListItemText, LinearProgress,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon, TrendingDown as TrendingDownIcon,
  AttachMoney as MoneyIcon, Assessment as AssessmentIcon,
  Warning as WarningIcon, CheckCircle as CheckIcon,
  Psychology as PsychologyIcon, Business as BusinessIcon,
  Timeline as TimelineIcon, Speed as SpeedIcon,
  Insights as InsightsIcon, Flag as FlagIcon
} from '@mui/icons-material';
import type { DailySummary, UserPerformance } from '../types/api';

// Helper function to format currency
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

// Helper function to format percentage
const formatPercentage = (value: number): string => {
  return `${value.toFixed(1)}%`;
};

// Helper function to get trend icon
const getTrendIcon = (trend: 'up' | 'down' | 'neutral') => {
  switch (trend) {
    case 'up': return <TrendingUpIcon color="success" />;
    case 'down': return <TrendingDownIcon color="error" />;
    default: return <TimelineIcon color="info" />;
  }
};

// Helper function to get priority color
const getPriorityColor = (priority: 'high' | 'medium' | 'low'): 'error' | 'warning' | 'info' => {
  switch (priority) {
    case 'high': return 'error';
    case 'medium': return 'warning';
    default: return 'info';
  }
};

export function ExecutiveBriefingPage() {
  const [selectedTimeframe, setSelectedTimeframe] = useState('today');

  // Fetch daily summary
  const { data: dailySummary, isLoading: isLoadingSummary } = useQuery<DailySummary | null>({
    queryKey: ['summaries', 'latest'],
    queryFn: async () => {
      try {
        const response = await summariesApi.getLatestDailySummary();
        return response.data;
      } catch (error) {
        console.error('Error fetching daily summary:', error);
        return null;
      }
    }
  });

  // Fetch forecast data
  const { data: forecastData, isLoading: isLoadingForecast } = useQuery({
    queryKey: ['forecasts', 'latest'],
    queryFn: async () => {
      try {
        const response = await forecastsApi.getLatestForecast();
        return response.data;
      } catch (error) {
        console.error('Error fetching forecast:', error);
        return null;
      }
    }
  });

  // Fetch team performance
  const { data: teamPerformance, isLoading: isLoadingPerformance } = useQuery<UserPerformance[]>({
    queryKey: ['performance', 'team'],
    queryFn: async () => {
      try {
        return await performanceApi.getUserPerformance();
      } catch (error) {
        console.error('Error fetching team performance:', error);
        return [];
      }
    }
  });

  // Fetch dashboard KPIs
  const { data: dashboardData, isLoading: isLoadingDashboard } = useQuery({
    queryKey: ['dashboard', 'kpis'],
    queryFn: async () => {
      try {
        const response = await dashboardApi.getKPIs();
        return response.data;
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        return null;
      }
    }
  });

  // Calculate executive insights
  const executiveInsights = useMemo(() => {
    const insights = [];

    // Pipeline health insights
    if (dashboardData) {
      const pipelineValue = dashboardData.pipeline_value || 0;
      if (pipelineValue > 1000000) {
        insights.push({
          type: 'success',
          priority: 'medium' as const,
          title: 'Strong Pipeline Health',
          description: `Pipeline value of ${formatCurrency(pipelineValue)} indicates healthy deal flow`,
          icon: <CheckIcon />
        });
      } else if (pipelineValue < 500000) {
        insights.push({
          type: 'warning',
          priority: 'high' as const,
          title: 'Pipeline Attention Needed',
          description: `Pipeline value of ${formatCurrency(pipelineValue)} is below target`,
          icon: <WarningIcon />
        });
      }
    }

    // Team performance insights
    if (teamPerformance && teamPerformance.length > 0) {
      const avgQuotaAttainment = teamPerformance.reduce((sum, p) => sum + p.quota_attainment, 0) / teamPerformance.length;
      const underperformers = teamPerformance.filter(p => p.quota_attainment < 70).length;

      if (avgQuotaAttainment >= 90) {
        insights.push({
          type: 'success',
          priority: 'low' as const,
          title: 'Excellent Team Performance',
          description: `Team averaging ${formatPercentage(avgQuotaAttainment)} quota attainment`,
          icon: <AssessmentIcon />
        });
      } else if (underperformers > teamPerformance.length * 0.3) {
        insights.push({
          type: 'warning',
          priority: 'high' as const,
          title: 'Team Performance Concern',
          description: `${underperformers} team members below 70% quota attainment`,
          icon: <WarningIcon />
        });
      }
    }

    // Forecast insights
    if (forecastData) {
      const delta = forecastData.ai_forecast - forecastData.team_commit;
      if (Math.abs(delta) > forecastData.team_commit * 0.1) {
        insights.push({
          type: 'info',
          priority: 'medium' as const,
          title: 'Forecast Variance Detected',
          description: `AI forecast differs from team commit by ${formatCurrency(Math.abs(delta))}`,
          icon: <InsightsIcon />
        });
      }
    }

    return insights;
  }, [dashboardData, teamPerformance, forecastData]);

  const isLoading = isLoadingSummary || isLoadingForecast || isLoadingPerformance || isLoadingDashboard;

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" component="h1">
            Executive Daily Briefing
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {new Date().toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Typography>
        </Box>
        <FormControl sx={{ minWidth: 150 }}>
          <InputLabel>Timeframe</InputLabel>
          <Select
            value={selectedTimeframe}
            label="Timeframe"
            onChange={(e) => setSelectedTimeframe(e.target.value)}
          >
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="week">This Week</MenuItem>
            <MenuItem value="month">This Month</MenuItem>
            <MenuItem value="quarter">This Quarter</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* AI Daily Summary */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <PsychologyIcon color="primary" fontSize="large" />
          <Typography variant="h5">AI Daily Summary</Typography>
        </Box>
        {dailySummary ? (
          <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
            {dailySummary.summary_text}
          </Typography>
        ) : (
          <Alert severity="info">
            No AI summary available for today. The system will generate one based on your latest activities.
          </Alert>
        )}
      </Paper>

      {/* Key Metrics Overview */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <MoneyIcon color="primary" fontSize="large" />
                <Box>
                  <Typography variant="h6">
                    {dashboardData ? formatCurrency(dashboardData.pipeline_value || 0) : '$0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Pipeline Value</Typography>
                  {getTrendIcon('up')}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <BusinessIcon color="success" fontSize="large" />
                <Box>
                  <Typography variant="h6">
                    {dashboardData?.active_deals_count || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Active Deals</Typography>
                  {getTrendIcon('up')}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AssessmentIcon color="warning" fontSize="large" />
                <Box>
                  <Typography variant="h6">
                    {teamPerformance ?
                      formatPercentage(teamPerformance.reduce((sum, p) => sum + p.quota_attainment, 0) / teamPerformance.length)
                      : '0%'
                    }
                  </Typography>
                  <Typography variant="body2" color="text.secondary">Avg Quota Attainment</Typography>
                  {getTrendIcon('neutral')}
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SpeedIcon color="info" fontSize="large" />
                <Box>
                  <Typography variant="h6">
                    {forecastData ? formatCurrency(forecastData.ai_forecast || 0) : '$0'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">AI Forecast</Typography>
                  {forecastData && forecastData.ai_forecast > forecastData.team_commit ?
                    getTrendIcon('up') : getTrendIcon('down')
                  }
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Strategic Insights & Alerts */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <InsightsIcon color="primary" fontSize="large" />
              <Typography variant="h6">Strategic Insights & Alerts</Typography>
            </Box>
            {executiveInsights.length === 0 ? (
              <Alert severity="info">
                No critical insights or alerts at this time. Your business metrics are within normal ranges.
              </Alert>
            ) : (
              <List>
                {executiveInsights.map((insight, index) => (
                  <ListItem key={index} divider={index < executiveInsights.length - 1}>
                    <ListItemIcon>
                      {insight.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" fontWeight="medium">
                            {insight.title}
                          </Typography>
                          <Chip
                            label={insight.priority}
                            size="small"
                            color={getPriorityColor(insight.priority)}
                          />
                        </Box>
                      }
                      secondary={insight.description}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <FlagIcon color="secondary" fontSize="large" />
              <Typography variant="h6">Quick Actions</Typography>
            </Box>
            <List>
              <ListItem>
                <ListItemText
                  primary="Review Forecast Variance"
                  secondary={forecastData ?
                    `AI vs Team: ${formatCurrency(Math.abs(forecastData.ai_forecast - forecastData.team_commit))} difference` :
                    'No forecast data available'
                  }
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Team Performance Review"
                  secondary={teamPerformance ?
                    `${teamPerformance.filter(p => p.quota_attainment < 70).length} members need attention` :
                    'No performance data available'
                  }
                />
              </ListItem>
              <Divider />
              <ListItem>
                <ListItemText
                  primary="Pipeline Health Check"
                  secondary={dashboardData ?
                    `${dashboardData.active_deals_count || 0} active deals in pipeline` :
                    'No pipeline data available'
                  }
                />
              </ListItem>
            </List>
          </Paper>
        </Grid>
      </Grid>

      {/* Forecast Analysis */}
      {forecastData && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Forecast Analysis
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Box sx={{ mb: 2 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Team Commit vs AI Forecast
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="body2">Team Commit:</Typography>
                  <Typography variant="h6" color="primary">
                    {formatCurrency(forecastData.team_commit)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                  <Typography variant="body2">AI Forecast:</Typography>
                  <Typography variant="h6" color="secondary">
                    {formatCurrency(forecastData.ai_forecast)}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="body2">Variance:</Typography>
                  <Typography
                    variant="h6"
                    color={forecastData.ai_forecast > forecastData.team_commit ? 'success.main' : 'error.main'}
                  >
                    {forecastData.ai_forecast > forecastData.team_commit ? '+' : ''}
                    {formatCurrency(forecastData.ai_forecast - forecastData.team_commit)}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  AI Justification
                </Typography>
                <Typography variant="body1">
                  {forecastData.justification || 'No justification provided for this forecast.'}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Team Performance Summary */}
      {teamPerformance && teamPerformance.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Team Performance Summary
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Quota Attainment Distribution
              </Typography>
              {(() => {
                const ranges = {
                  'Exceeding (100%+)': teamPerformance.filter(p => p.quota_attainment >= 100).length,
                  'On Track (80-99%)': teamPerformance.filter(p => p.quota_attainment >= 80 && p.quota_attainment < 100).length,
                  'At Risk (60-79%)': teamPerformance.filter(p => p.quota_attainment >= 60 && p.quota_attainment < 80).length,
                  'Needs Attention (<60%)': teamPerformance.filter(p => p.quota_attainment < 60).length,
                };

                return Object.entries(ranges).map(([range, count]) => (
                  <Box key={range} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{range}:</Typography>
                    <Chip
                      label={count}
                      size="small"
                      color={
                        range.includes('Exceeding') ? 'success' :
                          range.includes('On Track') ? 'info' :
                            range.includes('At Risk') ? 'warning' : 'error'
                      }
                    />
                  </Box>
                ));
              })()}
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Top Performers
              </Typography>
              {teamPerformance
                .sort((a, b) => b.quota_attainment - a.quota_attainment)
                .slice(0, 3)
                .map((performance, index) => (
                  <Box key={performance.id} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">
                      #{index + 1} User {performance.user_id}:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium" color="success.main">
                      {formatPercentage(performance.quota_attainment)}
                    </Typography>
                  </Box>
                ))}
            </Grid>
          </Grid>
        </Paper>
      )}
    </Container>
  );
}
